import { useEffect, useState } from "react";
import { getPkceChallenge } from "../actions/getPkceChallenge";
import { endpointsSpeckle } from "../actions/endpointsSpeckle";
import { authenticatedAxios } from "lib/actions/fetcher";
import { SpeckleAddAccountAuthStatus as AuthStatus } from "../../SpeckleImportWizard/types/SpeckleAddAccountAuthStatus";

export function usePopupAuthCallback(
  status: AuthStatus,
  onAuthStatusUpdate: (status: AuthStatus) => void,
  onAuthError: (message: string, error?: Error) => void,
) {
  const [popupUrl, setPopupUrl] = useState<string | null>(null);

  async function beginAuth(appId: number) {
    localStorage.setItem("speckleAppRegistrationId", appId.toString());

    const challenge = await getPkceChallenge();

    if (!challenge) {
      onAuthError("Error generating challenge.");
      onAuthStatusUpdate(AuthStatus.ERROR);
      return;
    }

    const url = await authenticatedAxios
      .get(endpointsSpeckle.requestAuthEndpoint(appId, challenge), {
        responseType: "text",
      })
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        onAuthError("Failed to get auth endpoint from api.", err);
        onAuthStatusUpdate(AuthStatus.ERROR);
        return null;
      });

    if (!url) {
      onAuthError("Error getting popup url from api.");
      onAuthStatusUpdate(AuthStatus.ERROR);
      return;
    }
    setPopupUrl(url);
    onAuthStatusUpdate(AuthStatus.AUTHENTICATING);
  }

  function onAuthResultMessage(
    event: MessageEvent<{ type: string; error?: boolean }>,
  ) {
    if (!event.data || event.data.type !== "authentication") {
      return;
    }
    if (event.origin !== globalThis.location.origin) {
      onAuthError(
        `Received message from unknown origin. Window origin: ${globalThis.location.origin} Event origin: ${event.origin}`,
      );
      onAuthStatusUpdate(AuthStatus.ERROR);
      return;
    }

    globalThis.removeEventListener("message", onAuthResultMessage);
    if (event.data.error) {
      onAuthError("Received error message from auth results popup.");
      onAuthStatusUpdate(AuthStatus.ERROR);
      return;
    }
    onAuthStatusUpdate(AuthStatus.COMPLETE);
  }

  useEffect(() => {
    if (status !== AuthStatus.AUTHENTICATING) return;
    if (!popupUrl) return;

    const popup = globalThis.open(popupUrl, "auth", "width=800,height=800");
    if (!popup) {
      onAuthError("Failed to open the popup window.");
      onAuthStatusUpdate(AuthStatus.POPUP_BLOCKED);
      return;
    }
    popup.focus();

    const popupCheckInterval = setInterval(() => {
      if (popup.closed) {
        clearInterval(popupCheckInterval);
        onAuthError("Popup closed before auth completed.");
        onAuthStatusUpdate(AuthStatus.ERROR);
      }
    }, 1000);

    globalThis.addEventListener("message", onAuthResultMessage, false);
    return () => {
      clearInterval(popupCheckInterval);
      globalThis.removeEventListener("message", onAuthResultMessage);
    };
  }, [status, popupUrl]);

  return { beginAuth };
}
